// C:\Users\Anish\xpr_survey\XPRSuvey-App\src\Components\Survey\Pages\UserManagement\UserList\User.jsx

import React, { useState, useEffect, useMemo } from "react";
import { Link, Outlet } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ChevronRight,
    ChevronLeft,
    ChevronDown,
    UserPlus,
    Users as UsersIcon,
    UserCheck,
    UserX,
    Search,
    Mars,
    Venus,
    Inbox,
} from "lucide-react";
import { GetAllUsers } from "../../../Services/SurveyService";
import { GetRoles } from "../../../Services/MasterDataService";

const ROLE_STYLES = {
    "Company Admin": "bg-[#c2a8f026] text-[#7f35b2]",
    "Company HR": "bg-blue-50 text-blue-600",
};

const USERS_PER_PAGE = 10;

const initials = (name) => name ? name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase() : "";

const buildPageSequence = (current, total, siblingCount = 1) => {
    const totalNumbersShown = siblingCount * 2 + 5;
    if (total <= totalNumbersShown) {
        return Array.from({ length: total }, (_, i) => i + 1);
    }

    const leftSibling = Math.max(current - siblingCount, 1);
    const rightSibling = Math.min(current + siblingCount, total);

    const showLeftEllipsis = leftSibling > 2;
    const showRightEllipsis = rightSibling < total - 1;

    const pages = [1];
    if (showLeftEllipsis) pages.push("...");
    for (let page = leftSibling; page <= rightSibling; page += 1) {
        if (page !== 1 && page !== total) pages.push(page);
    }
    if (showRightEllipsis) pages.push("...");
    pages.push(total);

    return pages;
};

const FilterSelect = ({ options, value, onChange }) => (
    <div className="relative">
        <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="appearance-none rounded-lg border border-gray-200 py-2.5 pl-3 pr-8 text-sm text-gray-700 outline-none focus:border-[#7f35b2] focus:ring-1 focus:ring-[#7f35b2]"
        >
            {options.map((opt) => (
                <option key={opt} value={opt}>
                    {opt}
                </option>
            ))}
        </select>
        <ChevronDown
            size={14}
            className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
        />
    </div>
);

/* --- Skeleton Loaders --- */
const StatCardSkeleton = () => (
    <div className="flex items-center gap-4 rounded-2xl border border-gray-100 bg-white p-5 shadow-md animate-pulse">
        <div className="h-12 w-12 shrink-0 rounded-full bg-gray-200" />
        <div className="flex-1 space-y-2">
            <div className="h-6 w-16 rounded bg-gray-200" />
            <div className="h-4 w-24 rounded bg-gray-200" />
            <div className="h-3 w-28 rounded bg-gray-200" />
        </div>
    </div>
);

const TableRowSkeleton = () => (
    <tr className="border-b border-gray-50 animate-pulse">
        <td className="py-3.5 pr-3"><div className="h-4 w-4 rounded bg-gray-200" /></td>
        <td className="py-3.5 pr-3">
            <div className="flex items-center gap-2.5">
                <div className="h-8 w-8 shrink-0 rounded-full bg-gray-200" />
                <div className="h-4 w-28 rounded bg-gray-200" />
            </div>
        </td>
        <td className="py-3.5 pr-3"><div className="h-4 w-36 rounded bg-gray-200" /></td>
        <td className="py-3.5 pr-3"><div className="h-4 w-28 rounded bg-gray-200" /></td>
        <td className="py-3.5 pr-3"><div className="h-6 w-20 rounded-full bg-gray-200" /></td>
        <td className="py-3.5 pr-3"><div className="h-6 w-16 rounded-full bg-gray-200" /></td>
        <td className="py-3.5 pr-3"><div className="h-4 w-24 rounded bg-gray-200" /></td>
    </tr>
);

const User = () => {
    const [search, setSearch] = useState("");
    const [UserList, setUserList] = useState([]);
    const [totalUsersCount, setTotalUsersCount] = useState(0);
    const [activeUsersCount, setActiveUsersCount] = useState(0);
    const [inactiveUsersCount, setInactiveUsersCount] = useState(0);
    const [totalHRCount, setTotalHRCount] = useState(0);
    const [loading, setloading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [roleOptions, setRoleOptions] = useState(["All Roles"]);
    const [roleFilter, setRoleFilter] = useState("All Roles");
    const [statusFilter, setStatusFilter] = useState("All Status");
    const [companyFilter, setCompanyFilter] = useState("All Companies");

    const STAT_CARDS = [
        {
            label: "Total Users",
            value: totalUsersCount,
            sub: "All registered users",
            icon: UsersIcon,
            iconBg: "bg-[#c2a8f033]",
            iconColor: "text-[#7f35b2]",
        },
        {
            label: "Active Users",
            value: activeUsersCount,
            sub: "Currently active users",
            icon: UserCheck,
            iconBg: "bg-blue-50",
            iconColor: "text-blue-500",
        },
        {
            label: "Inactive Users",
            value: inactiveUsersCount,
            sub: "Currently inactive users",
            icon: UserX,
            iconBg: "bg-orange-50",
            iconColor: "text-orange-500",
        },
        {
            label: "Total HR",
            value: totalHRCount,
            sub: "Users with HR role",
            icon: UsersIcon,
            iconBg: "bg-[#c2a8f033]",
            iconColor: "text-[#7f35b2]",
        },
    ];

    // useEffect(() => {
        const fetchUserList = async () => {
            try {
                setloading(true);
                const response = await GetAllUsers();
                if (response?.data?.status === "success") {
                    setUserList(response?.data?.usersList || []);
                    setTotalUsersCount(response?.data?.stats?.totalUsers || 0);
                    setActiveUsersCount(response?.data?.stats?.activeUsers || 0);
                    setInactiveUsersCount(response?.data?.stats?.inactiveUsers || 0);
                    setTotalHRCount(response?.data?.stats?.totalCompanyHR || 0);
                } else {
                    CustomToast("fail", "Something went wrong please try again later");
                }
            } catch (error) {
                CustomToast("fail", "An error occurred while fetching the Users");
            } finally {
                setloading(false);
            }
        };
        useEffect(() => {
            fetchUserList();
    }, []);


    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const response = await GetRoles();
                if (response?.data?.status === "success") {
                    const roleData = response?.data?.roleList || [];
                    setRoleOptions(["All Roles", ...roleData.map((r) => r.roleName)]);
                } else {
                    CustomToast("error", response?.data?.message || "Failed to fetch roles");
                }
            } catch (error) {
                CustomToast("error", "An error occurred while fetching roles");
            }
        };
        fetchRoles();
    }, []);



    const companyOptions = useMemo(() => {
        const uniqueCompanies = Array.from(
            new Set(UserList.map((u) => u.companyName).filter(Boolean))
        ).sort();
        return ["All Companies", ...uniqueCompanies];
    }, [UserList]);

    const filteredUsers = useMemo(() => {
        const query = search.trim().toLowerCase();

        return UserList.filter((user) => {
            const matchesSearch =
                !query ||
                (user.fullName || "").toLowerCase().includes(query) ||
                (user.email || "").toLowerCase().includes(query) ||
                (user.mobileNumber || "").toLowerCase().includes(query);

            const matchesRole =
                roleFilter === "All Roles" || user.roleName === roleFilter;

            const matchesCompany =
                companyFilter === "All Companies" || user.companyName === companyFilter;


            const matchesStatus =
                statusFilter === "All Status" ||
                (statusFilter === "Active" && user.isActive === true) ||
                (statusFilter === "Inactive" && user.isActive !== true);

            return matchesSearch && matchesRole && matchesCompany  && matchesStatus;
        });
    }, [UserList, search, roleFilter,  companyFilter ,statusFilter]);

    // Keep the current page valid whenever the filtered list size changes
    // (fresh fetch, or when search/filters narrow the results).
    useEffect(() => {
        setCurrentPage(1);
    }, [filteredUsers.length]);

    const totalPages = Math.max(1, Math.ceil(filteredUsers.length / USERS_PER_PAGE));

    // Clamp defensively — e.g. if the list shrinks while sitting on a later page.
    const safeCurrentPage = Math.min(currentPage, totalPages);

    const paginatedUsers = useMemo(() => {
        const start = (safeCurrentPage - 1) * USERS_PER_PAGE;
        return filteredUsers.slice(start, start + USERS_PER_PAGE);
    }, [filteredUsers, safeCurrentPage]);

    const pageSequence = useMemo(
        () => buildPageSequence(safeCurrentPage, totalPages),
        [safeCurrentPage, totalPages]
    );

    const rangeStart = filteredUsers.length === 0 ? 0 : (safeCurrentPage - 1) * USERS_PER_PAGE + 1;
    const rangeEnd = Math.min(safeCurrentPage * USERS_PER_PAGE, filteredUsers.length);

    const goToPage = (page) => {
        if (page < 1 || page > totalPages || page === safeCurrentPage) return;
        setCurrentPage(page);
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="mx-auto max-w-full"
        >
            {/* Page Header */}
            <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Users</h1>
                    <div className="mt-1 flex items-center gap-1.5 text-sm">
                        <span className="text-[#7f35b2]">Users</span>
                        <ChevronRight size={14} className="text-gray-400" />
                        <span className="text-gray-500">Manage Users</span>
                    </div>
                </div>
                <Link
                    to="/dashboard/users/add-user"
                    className="flex items-center gap-2 rounded-lg bg-[#7f35b2] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#3bb290]"
                >
                    <UserPlus size={16} />
                    Add New User
                </Link>
            </div>

            {/* Stat Cards */}
            <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {loading
                    ? Array.from({ length: 4 }).map((_, index) => (
                        <StatCardSkeleton key={index} />
                    ))
                    : STAT_CARDS.map((card) => {
                        const Icon = card.icon;
                        return (
                            <div
                                key={card.label}
                                className="flex items-center gap-4 rounded-2xl border border-gray-100 bg-white p-5 shadow-md"
                            >
                                <div
                                    className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${card.iconBg}`}
                                >
                                    <Icon size={22} className={card.iconColor} />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-gray-900">
                                        {card.value}
                                    </p>
                                    <p className="text-sm font-medium text-gray-700">
                                        {card.label}
                                    </p>
                                    <p className="text-xs text-gray-400">{card.sub}</p>
                                </div>
                            </div>
                        );
                    })}
            </div>

            {/* Table Card */}
            <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-md">
                {/* Filters */}
                <div className="mb-4 flex flex-wrap items-center  gap-3">
                    <div className="relative min-w-[220px] flex-1">
                        <Search
                            size={16}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                        />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by name, email or mobile..."
                            className="w-full rounded-lg border border-gray-200 py-2.5 pl-10 pr-3 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:border-[#7f35b2] focus:ring-1 focus:ring-[#7f35b2]"
                        />
                    </div>

                    <FilterSelect
                        options={companyOptions}
                        value={companyFilter}
                        onChange={setCompanyFilter}
                    />

                    {/* <FilterSelect options={["All Roles", "HR Admin", "HR Manager", "HR Executive"]} /> */}
                    <FilterSelect
                        options={roleOptions}
                        value={roleFilter}
                        onChange={setRoleFilter}
                    />
                    <FilterSelect
                        options={["All Status", "Active", "Inactive"]}
                        value={statusFilter}
                        onChange={setStatusFilter}
                    />

                    {/* <button className="flex items-center gap-2 rounded-lg border border-[#7f35b2] px-4 py-2.5 text-sm font-medium text-[#7f35b2] hover:bg-[#c2a8f033]">
                        <SlidersHorizontal size={16} />
                        Filters
                    </button> */}
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="border-b border-gray-100 text-xs font-semibold uppercase tracking-wide text-gray-400">
                                <th className="py-3 pr-3">NO.</th>
                                <th className="py-3 pr-3">User</th>
                                <th className="py-3 pr-3">Email</th>
                                <th className="py-3 pr-3">Mobile Number</th>
                                <th className="py-3 pr-3">Company Name</th>
                                <th className="py-3 pr-3">Role</th>
                                <th className="py-3 pr-3">Status</th>
                                <th className="py-3 pr-3">Created On</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                Array.from({ length: 5 }).map((_, idx) => (
                                    <TableRowSkeleton key={idx} />
                                ))
                            ) : paginatedUsers.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="py-12 text-center text-gray-400">
                                        <div className="flex flex-col items-center justify-center gap-2">
                                            <Inbox size={32} className="text-gray-300" />
                                            <p className="text-sm font-medium">No users found</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                paginatedUsers.map((user, idx) => (
                                    <tr key={user.id || idx} className="border-b border-gray-50 last:border-0">
                                        <td className="py-3 pr-3 text-gray-500">
                                            {(safeCurrentPage - 1) * USERS_PER_PAGE + idx + 1}
                                        </td>
                                        <td className="py-3 pr-3">
                                            <div className="flex items-center gap-2.5">
                                                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#c2a8f033] text-xs font-semibold text-[#7f35b2]">
                                                    {initials(user.fullName)}
                                                </div>
                                                <Link
                                                    to={`/dashboard/users/${user.hrUserUID}/assign-assessment`}
                                                    state={{ user }}
                                                    className="font-medium text-gray-900 hover:text-[#7f35b2] hover:underline"
                                                >
                                                    {user.fullName}
                                                </Link>
                                            </div>
                                        </td>
                                        <td className="py-3 pr-3 text-gray-500">{user.email}</td>
                                        <td className="py-3 pr-3 text-gray-500">{user.mobileNumber}</td>
                                        <td className="py-3 pr-3 text-gray-500">{user.companyName}</td>
                                        <td className="py-3 pr-3">
                                            <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${ROLE_STYLES[user.roleName] || "bg-gray-100 text-gray-600"}`}>
                                                {user.roleName}
                                            </span>
                                        </td>
                                        <td className="py-3 pr-3">
                                            <span className={`flex w-fit items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${user.isActive === true ? "bg-[#c2a8f026] text-[#7f35b2]" : "bg-orange-50 text-orange-500"}`}>
                                                <span className={`h-1.5 w-1.5 rounded-full ${user.isActive === true ? "bg-[#c2a8f026]0" : "bg-orange-500"}`} />
                                                {user.isActive === true ? "Active" : "InActive"}
                                            </span>
                                        </td>
                                        <td className="py-3 pr-3 text-gray-500">{user.createdOn}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm">
                    <p className="text-gray-500">
                        Showing {rangeStart} to {rangeEnd} of {filteredUsers.length} users
                    </p>
                    <div className="flex items-center gap-1.5">
                        <button
                            type="button"
                            onClick={() => goToPage(safeCurrentPage - 1)}
                            disabled={safeCurrentPage <= 1}
                            className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white"
                        >
                            <ChevronLeft size={15} />
                        </button>

                        {pageSequence.map((page, idx) =>
                            page === "..." ? (
                                <span key={`ellipsis-${idx}`} className="px-1 text-gray-400">
                                    ...
                                </span>
                            ) : (
                                <button
                                    type="button"
                                    key={page}
                                    onClick={() => goToPage(page)}
                                    className={`flex h-8 w-8 items-center justify-center rounded-lg text-sm font-medium ${page === safeCurrentPage
                                        ? "bg-[#7f35b2] text-white"
                                        : "border border-gray-200 text-gray-600 hover:bg-gray-50"
                                        }`}
                                >
                                    {page}
                                </button>
                            )
                        )}

                        <button
                            type="button"
                            onClick={() => goToPage(safeCurrentPage + 1)}
                            disabled={safeCurrentPage >= totalPages}
                            className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white"
                        >
                            <ChevronRight size={15} />
                        </button>
                    </div>
                </div>
            </div>

            <Outlet context={{ refreshUsers: fetchUserList }} />
        </motion.div>
    );
};

export default User;

